import { SideDrawer } from '@stern-ui/ui';
import { Close, Menu } from 'mdi-material-ui';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { MenuItem } from '@/components/Elements/MenuItem';
import { resetState } from '@/components/PatientDataTable/store/dataTableSlice';
import { MenuConfigObject, MenuConfigItem } from '@/config/menuConfig';
import { roles, SIDE_NAVIGATION_MENU } from '@/globalConsts';
import { trackEvent } from '@/lib/analytics';

export interface DrawerProps {
	className?: string;
	close: () => void;
	config: MenuConfigObject;
	isOpen: boolean;
	open: () => void;
	userRole: string | null;
}

export const Drawer = (props: DrawerProps) => {
	const { className, close, config, isOpen, open, userRole } = props;

	const dispatch = useDispatch();

	const handleClick = () => {
		dispatch(resetState());
		trackEvent('Sidebar menu item clicked');
	};

	const doShowHome = useMemo(() => {
		// Only users with care_agent' or 'study_site_admin' or 'flusniff_plus_approver' roles require the Home menu item
		if (
			userRole === roles.CARE_AGENT ||
			userRole === roles.STUDY_SITE_ADMIN ||
			userRole === roles.FLU_SNIFF_PLUS_APPROVER
		)
			return true;

		return false;
	}, [userRole]);

	const renderDashboardItems = (menu: MenuConfigItem[]) => {
		if (!menu) return null;
		return menu.map((dashboardItem, i) => {
			const { icon, link, title, testId } = dashboardItem;

			return (
				<NavLink
					key={i}
					to={link}
					title={title}
					children={({ isActive }) => {
						return (
							<MenuItem
								isExpanded={isOpen}
								icon={icon.nav}
								iconActive={icon.navActive}
								iconHover={icon.navHover}
								isActive={isActive}
								className='mt-2'
								onClick={!isActive ? handleClick : undefined}
							>
								{title}
							</MenuItem>
						);
					}}
					data-test-id={`${testId}-menu-item`}
				></NavLink>
			);
		});
	};

	const handleButtonClick = () => {
		if (isOpen) {
			close();
			trackEvent('Sidebar menu closed');
		} else {
			open();
			trackEvent('Sidebar menu expanded');
		}
	};

	return (
		<SideDrawer
			isExpanded={isOpen}
			containerClass={`bg-primaryBackground h-screen min-h-screen pt-5 overflow-x-hidden z-30 ${isOpen ? `${SIDE_NAVIGATION_MENU.WIDTH.EXPANDED}` : `${SIDE_NAVIGATION_MENU.WIDTH.DEFAULT}`} ${className}`}
			buttonClass='top-[1.25rem] right-[0.75rem]'
			handleClick={handleButtonClick}
			name='Main menu'
			openIcon={<Menu className='text-secondaryTextDark' />}
			closeIcon={<Close className='text-secondaryTextDark' />}
			dataTestId='side-navigation-menu'
		>
			<nav className='mx-2 mt-16'>
				{doShowHome && renderDashboardItems(config.home)}
				{renderDashboardItems(config[userRole as keyof typeof config])}
			</nav>
		</SideDrawer>
	);
};
